import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2, ChevronDown, ChevronRight, ExternalLink, Search } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { PageSpinner } from '@/components/ui/Spinner';
import { useDebounce } from '@/hooks/useDebounce';
import { adminApi } from '@/api/endpoints/admin';
import { metrosApi, type Metro } from '@/api/endpoints/metros';

interface Aggregate {
  outcome: string;
  reason: string;
  count: string;
  uniquePlaces: string;
}

interface LogEntry {
  id: string;
  googlePlaceId: string;
  placeName: string | null;
  placeAddress: string | null;
  placeTypes: string[];
  primaryType: string | null;
  latitude: string | null;
  longitude: string | null;
  websiteUrl: string | null;
  outcome: string;
  reason: string;
  metroId: string | null;
  searchLatitude: string | null;
  searchLongitude: string | null;
  searchRadius: number | null;
  rawPlaceData: Record<string, unknown> | null;
  createdAt: string;
}

const REASON_CONFIG: Record<string, { label: string; variant: 'success' | 'primary' | 'warning' | 'danger' | 'default' }> = {
  created: { label: 'Created', variant: 'success' },
  updated: { label: 'Updated', variant: 'primary' },
  no_website: { label: 'No Website', variant: 'warning' },
  not_bar: { label: 'Not Bar', variant: 'danger' },
  invalid_location: { label: 'Invalid Location', variant: 'danger' },
  error: { label: 'Error', variant: 'danger' },
};

const SINCE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

export function DiscoveryLogPage() {
  const queryClient = useQueryClient();
  const [metroId, setMetroId] = useState('');
  const [sinceDays, setSinceDays] = useState('30');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const pageSize = 25;

  // Fetch metros for filter
  const { data: metrosData } = useQuery({
    queryKey: ['metros'],
    queryFn: metrosApi.list,
  });

  // Fetch aggregates
  const { data: logData, isLoading, refetch } = useQuery({
    queryKey: ['discovery-log', metroId, sinceDays, selectedReason, debouncedSearch, page],
    queryFn: () =>
      adminApi.getDiscoveryLog({
        metroId: metroId || undefined,
        sinceDays: parseInt(sinceDays, 10),
        reason: selectedReason || undefined,
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
  });

  // Cleanup mutation
  const cleanupMutation = useMutation({
    mutationFn: (daysOld: number) => adminApi.cleanupDiscoveryLog(daysOld),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['discovery-log'] });
      toast.success(data.message);
    },
    onError: () => {
      toast.error('Failed to clean up discovery log');
    },
  });

  const metros = metrosData?.metros || [];
  const aggregates: Aggregate[] = logData?.aggregates || [];
  const entries: LogEntry[] = logData?.entries || [];

  // Compute totals from aggregates
  const totalAccepted = aggregates
    .filter((a) => a.outcome === 'accepted')
    .reduce((sum, a) => sum + parseInt(a.count, 10), 0);
  const totalRejected = aggregates
    .filter((a) => a.outcome === 'rejected')
    .reduce((sum, a) => sum + parseInt(a.count, 10), 0);
  const total = totalAccepted + totalRejected;

  function handleReasonClick(reason: string) {
    setSelectedReason(selectedReason === reason ? null : reason);
    setPage(0);
  }

  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  if (isLoading && !logData) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Discovery Log</h1>
          <p className="text-[var(--color-muted)]">
            Track which bars were accepted or rejected during discovery
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name..."
            className="pl-9"
          />
        </div>
        <div className="w-48">
          <Select
            value={metroId}
            onChange={(e) => {
              setMetroId(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All metros</option>
            {metros.map((metro: Metro) => (
              <option key={metro.id} value={metro.id}>
                {metro.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-40">
          <Select
            value={sinceDays}
            onChange={(e) => {
              setSinceDays(e.target.value);
              setPage(0);
            }}
          >
            {SINCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {total === 0 ? (
            <p className="text-[var(--color-muted)] text-center py-4">
              No discovery data for this period. Run a discovery job first.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Top-level totals */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-lg bg-[var(--color-border)]">
                  <div className="text-2xl font-bold">{total.toLocaleString()}</div>
                  <div className="text-sm text-[var(--color-muted)]">Total Places</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-500/10">
                  <div className="text-2xl font-bold text-green-500">
                    {totalAccepted.toLocaleString()}
                  </div>
                  <div className="text-sm text-[var(--color-muted)]">Accepted</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-red-500/10">
                  <div className="text-2xl font-bold text-red-500">
                    {totalRejected.toLocaleString()}
                  </div>
                  <div className="text-sm text-[var(--color-muted)]">Rejected</div>
                </div>
              </div>

              {/* Per-reason breakdown — clickable to filter */}
              <div className="flex flex-wrap gap-2">
                {aggregates.map((agg) => {
                  const config = REASON_CONFIG[agg.reason] || {
                    label: agg.reason,
                    variant: 'default' as const,
                  };
                  const isActive = selectedReason === agg.reason;
                  return (
                    <button
                      key={`${agg.outcome}-${agg.reason}`}
                      onClick={() => handleReasonClick(agg.reason)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                        isActive
                          ? 'border-accent-primary bg-accent-primary/10'
                          : 'border-[var(--color-border)] hover:bg-[var(--color-border)]'
                      }`}
                    >
                      <Badge variant={config.variant}>{config.label}</Badge>
                      <span className="font-semibold">{parseInt(agg.count, 10).toLocaleString()}</span>
                      <span className="text-[var(--color-muted)]">
                        ({parseInt(agg.uniquePlaces, 10).toLocaleString()} unique)
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Table — shown when a reason is selected or search is active */}
      {(selectedReason || debouncedSearch) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Entries
              {selectedReason && (
                <>
                  : <Badge variant={REASON_CONFIG[selectedReason]?.variant || 'default'}>
                    {REASON_CONFIG[selectedReason]?.label || selectedReason}
                  </Badge>
                  <button
                    onClick={() => setSelectedReason(null)}
                    className="ml-2 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
                  >
                    Clear filter
                  </button>
                </>
              )}
              {debouncedSearch && (
                <span className="text-sm font-normal text-[var(--color-muted)]">
                  matching "{debouncedSearch}"
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <p className="text-[var(--color-muted)] text-center py-4">
                No entries found for this filter.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--color-border)] text-left">
                        <th className="py-2 pr-3 font-medium text-[var(--color-muted)]">Name</th>
                        <th className="py-2 pr-3 font-medium text-[var(--color-muted)]">Address</th>
                        <th className="py-2 pr-3 font-medium text-[var(--color-muted)]">Type</th>
                        <th className="py-2 pr-3 font-medium text-[var(--color-muted)]">Website</th>
                        <th className="py-2 pr-3 font-medium text-[var(--color-muted)]">Date</th>
                        <th className="py-2 font-medium text-[var(--color-muted)]">Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => {
                        const isExpanded = expandedRows.has(entry.id);
                        return (
                          <tr
                            key={entry.id}
                            className="border-b border-[var(--color-border)] last:border-0"
                          >
                            <td className="py-2 pr-3 font-medium">
                              {entry.placeName || 'Unknown'}
                            </td>
                            <td className="py-2 pr-3 text-[var(--color-muted)] max-w-[200px] truncate">
                              {entry.placeAddress || '-'}
                            </td>
                            <td className="py-2 pr-3">
                              <Badge variant="default">{entry.primaryType || '-'}</Badge>
                            </td>
                            <td className="py-2 pr-3">
                              {entry.websiteUrl ? (
                                <a
                                  href={entry.websiteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-accent-primary hover:underline inline-flex items-center gap-1"
                                >
                                  Link
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <span className="text-[var(--color-muted)]">-</span>
                              )}
                            </td>
                            <td className="py-2 pr-3 text-[var(--color-muted)] whitespace-nowrap">
                              {new Date(entry.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-2">
                              {entry.rawPlaceData && (
                                <button
                                  onClick={() => toggleRow(entry.id)}
                                  className="text-[var(--color-muted)] hover:text-[var(--color-text)] inline-flex items-center gap-1 text-xs"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3" />
                                  )}
                                  Raw
                                </button>
                              )}
                              {isExpanded && entry.rawPlaceData && (
                                <pre className="mt-1 p-2 rounded bg-[var(--color-border)] text-xs overflow-x-auto max-w-sm">
                                  {JSON.stringify(entry.rawPlaceData, null, 2)}
                                </pre>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--color-border)]">
                  <span className="text-sm text-[var(--color-muted)]">
                    Page {page + 1} ({entries.length} entries)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={entries.length < pageSize}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cleanup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-[var(--color-muted)]" />
            Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <p className="text-sm text-[var(--color-muted)]">
              Delete discovery log entries older than 90 days
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => cleanupMutation.mutate(90)}
              disabled={cleanupMutation.isPending}
            >
              {cleanupMutation.isPending ? 'Cleaning...' : 'Clean Up'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
